import {
  PokemonType,
  SkillData,
  SkillType,
  SkillRangeType,
  SkillEvent_Vfx,
  SkillEvent_MeleeHit,
  StatusEffect,
  SkillEvent_GapClose,
} from "types/battle-engine/battle";
import { parseSkillIdToName } from "../utils/parse-id-to-name";
import { GapCloseType } from "types/battle-engine/battle";
import { msToTicks } from "../utils/ms-to-ticks";

// Gap Closers
export const [
  QUICK_ATTACK,
  BLAZE_KICK,
  AQUA_JET,
  VOLT_SWITCH,
  JUMP_KICK,
  BRAVE_BIRD,
  LUNGE,
  SHADOW_SNEAK,
  DRAGON_RUSH,
  PURSUIT,
  SMART_STRIKE,
] = [
  {
    id: "quick-attack",
    type: PokemonType.NORMAL,
    gap_close_type: GapCloseType.TARGET_NEAREST,
    vfx_sprite: "/assets/spells/impact/tiny-white-1.png",
    impact_sprite: "/assets/spells/impact/melee-red-3.png",
    status_effects: [],
  },
  {
    id: "blaze-kick",
    type: PokemonType.FIRE,
    gap_close_type: GapCloseType.TARGET_NEAREST,
    vfx_sprite: "/assets/spells/impact/tiny-white-1.png",
    impact_sprite: "/assets/spells/impact/melee-red-3.png",
    status_effects: [],
  },
  {
    id: "aqua-jet",
    description: "Dashes to the nearest enemy.",
    type: PokemonType.WATER,
    gap_close_type: GapCloseType.TARGET_NEAREST,
    vfx_sprite: "/assets/spells/impact/tiny-white-1.png",
    impact_sprite: "/assets/spells/impact/melee-red-3.png",
    status_effects: [],
  },
  {
    id: "volt-switch",
    description: "Dashes to the nearest enemy.",
    type: PokemonType.ELECTRIC,
    gap_close_type: GapCloseType.TARGET_NEAREST,
    vfx_sprite: "/assets/spells/impact/tiny-white-1.png",
    impact_sprite: "/assets/spells/impact/melee-red-3.png",
    status_effects: [],
  },
  {
    id: "jump-kick",
    description: "Dashes to the furthest enemy.",
    type: PokemonType.FIGHTING,
    gap_close_type: GapCloseType.TARGET_FURTHEST,
    vfx_sprite: "/assets/spells/impact/tiny-white-1.png",
    impact_sprite: "/assets/spells/impact/melee-red-3.png",
    status_effects: [],
  },
  {
    id: "brave-bird",
    description: "Dashes to the furthest enemy.",
    type: PokemonType.FLYING,
    gap_close_type: GapCloseType.TARGET_FURTHEST,
    vfx_sprite: "/assets/spells/impact/tiny-white-1.png",
    impact_sprite: "/assets/spells/impact/melee-red-3.png",
    status_effects: [],
  },
  {
    id: "lunge",
    description: "Dashes to the furthest enemy.",
    type: PokemonType.BUG,
    gap_close_type: GapCloseType.TARGET_FURTHEST,
    vfx_sprite: "/assets/spells/impact/tiny-white-1.png",
    impact_sprite: "/assets/spells/impact/melee-red-3.png",
    status_effects: [],
  },
  {
    id: "shadow-sneak",
    description: "Dashes to the lowest HP enemy and strikes fear.",
    type: PokemonType.GHOST,
    gap_close_type: GapCloseType.TARGET_LOW_HP,
    vfx_sprite: "/assets/spells/impact/tiny-white-1.png",
    impact_sprite: "/assets/spells/impact/melee-red-3.png",
    status_effects: [
      {
        status: StatusEffect.FEAR,
        duration_ticks: 310,
        intensity: 1,
      },
    ],
  },
  {
    id: "dragon-rush",
    description: "Dashes to the furthest target and slows them.",
    type: PokemonType.DRAGON,
    gap_close_type: GapCloseType.TARGET_FURTHEST,
    vfx_sprite: "/assets/spells/impact/tiny-white-1.png",
    impact_sprite: "/assets/spells/impact/melee-red-3.png",
    status_effects: [
      {
        status: StatusEffect.SPEED_DOWN,
        duration_ticks: 310,
        intensity: 1,
      },
    ],
  },
  {
    id: "pursuit",
    description: "Dashes to the lowest HP target and strikes fear.",
    type: PokemonType.DARK,
    gap_close_type: GapCloseType.TARGET_LOW_HP,
    vfx_sprite: "/assets/spells/impact/tiny-white-1.png",
    impact_sprite: "/assets/spells/impact/melee-red-3.png",
    status_effects: [
      {
        status: StatusEffect.FEAR,
        duration_ticks: 310,
        intensity: 1,
      },
    ],
  },
  {
    id: "smart-strike",
    description: "A precise dash to the lowest HP target.",
    type: PokemonType.STEEL,
    gap_close_type: GapCloseType.TARGET_LOW_HP,
    vfx_sprite: "/assets/spells/impact/tiny-white-1.png",
    impact_sprite: "/assets/spells/impact/melee-red-3.png",
    status_effects: [],
  },
].map((skill: any) => ({
  id: skill.id,
  event_checks:
    "event_checks" in skill
      ? (skill.event_checks as SkillData["event_checks"])
      : undefined,
  momentum:
    "momentum" in skill ? (skill.momentum as SkillData["momentum"]) : undefined,
  team_conditions:
    "team_conditions" in skill
      ? (skill.team_conditions as SkillData["team_conditions"])
      : undefined,
  starts_active: false,
  name: parseSkillIdToName(skill.id),
  description: skill.description || "",
  power: 10,
  pokemon_type: skill.type,
  types: [SkillType.DAMAGE, SkillType.GAP_CLOSE],
  cooldown_ticks: msToTicks(5000),
  mana_cost: 10,
  range: {
    type: SkillRangeType.POINT_TARGET,
    value: 8,
  },
  on_hit: [
    {
      __typename: "Vfx",
      duration_ticks: 1,
      target: {
        type: "enemy",
        id: 0,
      },
      status_effects: [],
      starting_tick: 0,
      sprite_path: skill.impact_sprite,
      sprite_scale: 1,
      sprite_alpha: 1,
      blocks_event_pipeline: false,
    } as SkillEvent_Vfx,
  ],
  on_cast: [
    {
      __typename: "Vfx",
      duration_ticks: 30,
      target: {
        type: "self",
        id: 0,
      },
      status_effects: [],
      starting_tick: 0,
      sprite_path: skill.vfx_sprite,
      sprite_scale: 1,
      sprite_alpha: 1,
      blocks_event_pipeline: false,
    } as SkillEvent_Vfx,
    {
      __typename: "GapClose",
      duration_ticks: 30,
      target: {
        type: "enemy",
        id: 0,
      },
      status_effects: skill.status_effects,
      starting_tick: 0,
      damage: 15,
      range: 8,
      gap_close_type: skill.gap_close_type,
      blocks_event_pipeline: true,
    } as SkillEvent_GapClose,
    {
      __typename: "Vfx",
      duration_ticks: 1,
      target: {
        type: "enemy",
        id: 0,
      },
      status_effects: [],
      starting_tick: 0,
      sprite_path: skill.impact_sprite,
      sprite_scale: 1,
      sprite_alpha: 1,
      blocks_event_pipeline: false,
    } as SkillEvent_Vfx,
    {
      __typename: "MeleeHit",
      duration_ticks: 25,
      target: {
        type: "enemy",
        id: 0,
      },
      status_effects: [],
      starting_tick: 0,
      damage: 0,
      blocks_event_pipeline: true,
    } as SkillEvent_MeleeHit,
  ],
}));